import { ProductItem, RefEntity, TableRow, TFunc } from './types';
import { additionalVariableOptionIds } from './cascadeOptions';

/**
 * Resolve a possibly-populated reference field to a display string.
 * Handles populated objects ({ name }), plain strings, and null.
 */
export const resolveName = (value: unknown): string => {
  if (value === null || value === undefined) return '';
  if (typeof value === 'object') {
    const ref = value as RefEntity;
    return ref.name ?? ref.keyName ?? ref.label ?? '';
  }
  return String(value);
};

/**
 * Resolve a reference field to its id (for dropdown editing/saving).
 * Prefers a populated object's _id, else the raw value if it is an id string.
 */
export const resolveId = (value: unknown): string => {
  if (value === null || value === undefined) return '';
  if (Array.isArray(value)) return resolveId(value[0]);
  if (typeof value === 'object') return String((value as RefEntity)._id ?? '');
  return String(value);
};

/** True for a 24-char hex Mongo ObjectId string — used to tell a raw id from a display name. */
const isObjectId = (value: string): boolean => /^[a-f\d]{24}$/i.test(value);

// The list API returns reference fields as display NAMES, so a name must never land in an id
// field: saving one makes Mongo reject the whole product ("must be a 24 character hex string").
const resolveRefId = (value: unknown): string => {
  const id = resolveId(value);
  return isObjectId(id) ? id : '';
};

/** Resolve an array field of refs/ids into an id string[] for inline multi-select editing. */
const resolveIdArray = (value: unknown): string[] => (Array.isArray(value) ? value.map((entry) => resolveId(entry)).filter(Boolean) : []);

/**
 * The array counterpart of {@link resolveRefId}. The list API returns array reference fields as
 * display NAMES just as it does scalar ones, and the server casts these to ObjectId — so a name
 * left in place makes Mongo reject the entire product save. Entries that are not ids are dropped.
 *
 * Only for fields the server ObjectId-casts. `soldCountries` holds slugs, not ids, and must keep
 * using {@link resolveIdArray}.
 * @param value the raw array from the list API
 * @returns the entries that are real ObjectIds
 */
const resolveRefIdArray = (value: unknown): string[] => resolveIdArray(value).filter(isObjectId);

const asRecord = (product: ProductItem) => product as unknown as Record<string, unknown>;

/**
 * Normalise the backend's Product Number Type into the 'Manual'/'Auto' string the
 * table view's dropdown (option _ids) matches on. The API stores/returns a boolean
 * (false = Manual, true = Auto — see buildProductPayload), so on edit the raw value
 * arrives as a boolean; passing it through unmapped leaves the dropdown unmatched
 * ("type not set"). A legacy string value is preserved as-is.
 */
const resolveProductNumberType = (value: unknown): string => {
  if (value === 'Auto' || value === true) return 'Auto';
  if (value === 'Manual' || value === false) return 'Manual';
  return typeof value === 'string' && value ? value : 'Manual';
};

/**
 * Resolve the Product Number Type into the DISPLAY label a read-only list row shows
 * ('Auto Generated' / 'Manual'). The `__label` accessor renders this verbatim, so it must
 * carry the translated name — not the 'Auto'/'Manual' option id the editable cell matches on
 * (see the productNumberType options in the table view's selectOptions).
 */
const resolveProductNumberTypeLabel = (value: unknown, t?: TFunc): string => {
  const key = resolveProductNumberType(value) === 'Auto' ? 'PRODUCT_NUMBER_AUTO_GENERATED' : 'PRODUCT_NUMBER_MANUAL';
  return t ? t(`COMPONENT.PRODUCT_INFORMATION.PRODUCT_MODAL.${key}`) : '';
};

/**
 * Summarise an array of ids using a supplied id→name lookup (e.g. supplierFactories, whose
 * list-row value is an array of factory ids with no populated names). Ids with no map entry
 * and no populated name are dropped rather than counted, so a row whose factories are all
 * unresolvable (deleted factory, or master not yet loaded) renders blank instead of a bare
 * selection count.
 */
const summariseIdsByMap = (value: unknown, nameById: Record<string, string> | undefined): string => {
  if (!Array.isArray(value) || value.length === 0) return '';
  const names = value
    .map((entry) => {
      const mapped = nameById?.[resolveId(entry)];
      if (mapped) return mapped;
      return typeof entry === 'object' && entry !== null ? resolveName(entry) : '';
    })
    .filter(Boolean);
  return names.join(', ');
};

/**
 * Resolve a single reference value that may already BE a display name (the list API projects
 * `brand: '$brand.name'`) or may be a bare id (the edit-detail API returns the raw ref, and a
 * saved row is replaced with that detail). A populated object yields its name; a plain name
 * passes through; an id resolves via the map and is dropped if absent, so an id never renders.
 */
const resolveDisplayName = (value: unknown, nameById?: Record<string, string>): string => {
  if (value === null || value === undefined) return '';
  if (Array.isArray(value)) return resolveDisplayName(value[0], nameById);
  if (typeof value === 'object') {
    const populated = resolveName(value);
    if (populated) return populated;
    const id = resolveId(value);
    return (id && nameById?.[id]) || '';
  }
  const raw = String(value);
  const mapped = nameById?.[raw];
  if (mapped) return mapped;
  return isObjectId(raw) ? '' : raw;
};

/**
 * Summarise an array whose entries may already BE display names (the list API resolves
 * suppliers server-side) or may be bare ids (the edit-detail API does not). A name is used
 * as-is; anything that looks like an id is resolved via the map, and dropped if absent —
 * so an id never leaks into a name column.
 */
const summariseNames = (value: unknown, nameById: Record<string, string> | undefined): string => {
  if (!Array.isArray(value) || value.length === 0) return '';
  const names = value.map((entry) => resolveDisplayName(entry, nameById)).filter(Boolean);
  return names.join(', ');
};

/**
 * Build an id→name lookup from a master list of { _id, name } entries.
 * @param list the master array (any non-array value yields an empty map)
 * @param nameKey the entry's display-name property (defaults to `name`)
 */
export const buildNameMap = (list: unknown, nameKey = 'name'): Record<string, string> => {
  const map: Record<string, string> = {};
  if (!Array.isArray(list)) return map;
  list.forEach((entry) => {
    const id = (entry as RefEntity)?._id;
    if (id) map[String(id)] = ((entry as Record<string, unknown>)?.[nameKey] as string) ?? '';
  });
  return map;
};

/**
 * Master-derived id→name lookups, keyed by row field name. Needed because the list API
 * returns most reference fields as resolved NAMES while the edit-detail API returns bare
 * ids — a saved row carries detail data, so its labels must be resolved client-side.
 * A missing map degrades that field to a blank cell rather than leaking an id.
 */
export type NameMaps = Record<string, Record<string, string> | undefined>;

/**
 * Build the multi-select and scalar "extra" columns: id-arrays for editing plus a
 * `<field>__label` summary for the read-only list display.
 */
const mapExtraFields = (product: ProductItem, nameMaps?: NameMaps, t?: TFunc): Record<string, unknown> => {
  const raw = asRecord(product);
  return {
    productNumberType: resolveProductNumberType(raw.productNumberType),
    productNumberType__label: resolveProductNumberTypeLabel(raw.productNumberType, t),
    counterNo: (raw.counterNo as string) ?? '',
    isLicensedProduct: Boolean(raw.isLicensedProduct),
    sizeRangeId: resolveRefIdArray(raw.sizeRangeId ?? raw.sizeRanges),
    sizeRangeId__label: summariseNames(raw.sizeRangeId, nameMaps?.sizeRangeId) || summariseNames(raw.sizeRanges, nameMaps?.sizeRangeId),
    sizeRangeSizes: resolveRefIdArray(raw.sizeRangeSizes),
    sizeRangeSizes__label: summariseNames(raw.sizeRangeSizes, nameMaps?.sizeRangeSizes),
    legLengths: resolveIdArray(product.legLengths),
    legLengths__label: summariseNames(product.legLengths, nameMaps?.legLengths),
    colourLibraries: resolveIdArray(product.colourLibraries),
    colourLibraries__label: summariseNames(product.colourLibraries, nameMaps?.colourLibraries),
    suppliers: resolveIdArray(product.suppliers),
    suppliers__label: summariseNames(product.suppliers, nameMaps?.suppliers),
    /* Absent means "never set", which the schema defaults to true — only an explicit false hides
       the product from the supplier portal. */
    visibleToSupplier: raw.visibleToSupplier !== false,
    supplierFactories: resolveIdArray(raw.supplierFactories),
    supplierFactories__label: summariseIdsByMap(raw.supplierFactories, nameMaps?.supplierFactories),
    productTypeProcesses: resolveIdArray(raw.productTypeProcesses),
    productTypeProcesses__label: summariseNames(raw.productTypeProcesses, nameMaps?.productTypeProcesses),
    soldCountries: resolveIdArray(raw.soldCountries),
    soldCountries__label: summariseIdsByMap(raw.soldCountries, nameMaps?.soldCountries),
    transport: (raw.transport as string) ?? '',
    transport__label: resolveDisplayName(raw.transport, nameMaps?.transport),
    status: resolveId(raw.status),
    status__label: product.statusName || resolveDisplayName(product.status ?? raw.status, nameMaps?.status),
    tags: Array.isArray(raw.tags) ? (raw.tags as string[]).join(', ') : (raw.tags as string) ?? '',
    /* Persisted on the product as `tagDescription` (same field the Create/Edit Product screen writes),
       so the Description cell's tick survives a save and a reload instead of resetting to unticked. */
    tagDescription: Boolean(raw.tagDescription),
    image: product.image ?? '',
    fullImg: (asRecord(product).fullImg as string) ?? '',
    thumbNailImg: (asRecord(product).thumbNailImg as string) ?? '',
    additionalVariables: (raw.additionalVariables as Record<string, unknown> | null) ?? null,
    additionalVariables__label: summariseIdsByMap(additionalVariableOptionIds(raw.additionalVariables), nameMaps?.additionalVariables),
  };
};

/**
 * Resolve the reference/id-backed dropdown columns. Stores the id so inline edit
 * pre-selects and saves correctly; the column's valueFormatter maps id -> name for
 * the read-only display.
 */
const mapReferenceFields = (product: ProductItem): Record<string, string> => ({
  brandId: resolveRefId(product.brandId ?? product.brand),
  subBrandId: resolveRefId(product.subBrandId ?? product.subBrand),
  licensorId: resolveRefId(product.licensorId ?? product.licensor),
  licenseId: resolveRefId(product.licenseId ?? product.license),
  likenessId: resolveRefId(product.likenessId ?? product.likeness),
  supplierType: resolveRefId(product.supplierType),
  catOneId: resolveRefId(product.catOneId),
  catTwoId: resolveRefId(product.catTwoId),
  catThreeId: resolveRefId(product.catThreeId),
  catFourId: resolveRefId(product.catFourId),
  seasonId: resolveRefId(product.seasonId),
});

/**
 * Read-only display names for the dropdown columns, keyed by `<field>__label`.
 * Each value may arrive as a name (list API) or a bare id (edit-detail API), so every field
 * resolves through its master map rather than stringifying the raw value.
 */
const mapReferenceLabels = (product: ProductItem, nameMaps?: NameMaps): Record<string, string> => ({
  brandId__label: resolveDisplayName(product.brand ?? product.brandId, nameMaps?.brandId),
  subBrandId__label: resolveDisplayName(product.subBrand ?? product.subBrandId, nameMaps?.subBrandId),
  licensorId__label: resolveDisplayName(product.licensor ?? product.licensorId, nameMaps?.licensorId),
  licenseId__label: resolveDisplayName(product.license ?? product.licenseId, nameMaps?.licenseId),
  likenessId__label: resolveDisplayName(product.likeness ?? product.likenessId, nameMaps?.likenessId),
  supplierType__label: resolveDisplayName(product.supplierType, nameMaps?.supplierType),
  catOneId__label: resolveDisplayName(product.catOne ?? product.catOneId, nameMaps?.catOneId),
  catTwoId__label: resolveDisplayName(product.catTwo ?? product.catTwoId, nameMaps?.catTwoId),
  catThreeId__label: resolveDisplayName(product.catThree ?? product.catThreeId, nameMaps?.catThreeId),
  catFourId__label: resolveDisplayName(product.catFour ?? product.catFourId, nameMaps?.catFourId),
  seasonId__label: resolveDisplayName(product.season ?? product.seasonId, nameMaps?.seasonId),
});

/**
 * Resolve a dynamic-column entry's ATTRIBUTE id (mirrors ProductModal's getEntryAttrId):
 * the entry may carry the id directly (`_id`) or under a populated/plain `colId`. This must
 * match the single custom-attribute master's `_id` so the mapped value lands in the right
 * `dyn_<attributeId>` column.
 */
const dynamicAttrId = (entry: { _id?: unknown; colId?: unknown }): string => {
  if (entry?._id) return String(entry._id);
  const colId = entry?.colId as { _id?: unknown } | string | undefined;
  if (colId && typeof colId === 'object' && colId._id) return String(colId._id);
  if (typeof colId === 'string') return colId;
  return '';
};

/**
 * Flatten a product's dynamic columns into dyn_<attributeId> row keys. Dropdown/radio
 * attributes carry a selected option: store its id under dyn_<id> (so the select cell
 * pre-selects and edit saves the id → valueId) plus its name under dyn_<id>__label for the
 * read-only list display. Text/date attributes store their string under dyn_<id> (the value
 * is both edited and displayed as-is).
 */
const mapDynamicFields = (product: ProductItem): Record<string, string> => {
  if (!Array.isArray(product.dynamicColumn)) return {};
  const out: Record<string, string> = {};
  product.dynamicColumn.forEach((field) => {
    const id = dynamicAttrId(field);
    if (!id) return;
    const key = `dyn_${id}`;
    if (field.selectedOpt?._id) {
      out[key] = String(field.selectedOpt._id);
      out[`${key}__label`] = field.selectedOpt.keyName ?? '';
    } else {
      out[key] = field.valueText ?? '';
    }
  });
  return out;
};

/**
 * Flatten a product's selected multi-custom-attribute values into mca_<colId> id
 * arrays (for inline multi-select editing) plus a `mca_<colId>__label` display summary.
 * The label resolves the selected value ids to their NAMES via the per-column
 * `mca_<colId>` name map (built from selectOptions), joined with ", " — mirroring the
 * other multi-select columns (colourways, suppliers). Without the map (not yet loaded)
 * a value id never leaks; the cell renders blank instead.
 */
const mapMultiCustomFields = (product: ProductItem, nameMaps?: NameMaps): Record<string, unknown> => {
  const selected = (asRecord(product).multiCustomAttribute as Array<{ colId: string; valId: string[] }>) || [];
  if (!Array.isArray(selected)) return {};
  const row: Record<string, unknown> = {};
  selected.forEach((entry) => {
    const ids = Array.isArray(entry.valId) ? entry.valId : [];
    row[`mca_${entry.colId}`] = ids;
    row[`mca_${entry.colId}__label`] = summariseNames(ids, nameMaps?.[`mca_${entry.colId}`]);
  });
  return row;
};

/**
 * Map a product (dev order) list item to an AG Grid row for the table view.
 * The `t` option is accepted for a stable call signature across callers but is not
 * used here (labels are precomputed on the product). `nameMaps` carries optional
 * master-derived id→name lookups (suppliers, factories) used to display NAMES on
 * read-only list rows, since the product stores only ids for those fields.
 */
export const mapProductToRow = (product: ProductItem, _opts?: { t: TFunc; nameMaps?: NameMaps }): TableRow => ({
  _id: product._id,
  number: product.number ?? '',
  description: product.description ?? '',
  tagDescription: Boolean(product.tagDescription),
  month: product.month ?? '',
  month__label: product.month != null ? String(product.month) : '',
  year: product.year ?? '',
  year__label: product.year != null ? String(product.year) : '',
  ...mapReferenceFields(product),
  ...mapReferenceLabels(product, _opts?.nameMaps),
  ...mapExtraFields(product, _opts?.nameMaps, _opts?.t),
  ...mapDynamicFields(product),
  ...mapMultiCustomFields(product, _opts?.nameMaps),
  __product: product,
});

/**
 * The products the table renders. A reload empties the list before the next page arrives; keeping
 * the rows already on screen until then lets the grid stay mounted under its loader instead of
 * being thrown away and rebuilt.
 * @param fresh the list as the provider holds it now
 * @param previous the products last rendered
 * @param isReloading whether a list request is in flight
 * @returns the products to render
 */
export const productsToShow = (fresh: ProductItem[], previous: ProductItem[], isReloading: boolean): ProductItem[] =>
  isReloading && !fresh.length ? previous : fresh;
